import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { cookieConsentLabels, footerLabels, privacyLabels } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNativeExpandableSheetProps } from "@/lib/native/useNativeExpandableSheetProps";
import { cn } from "@/lib/utils";
import { openCookieConsentSettings } from "@/lib/cookieConsent";
import { Cookie } from "lucide-react";

const formatBold = (s: string) => s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

export type PrivacyPolicyTab = "privacy" | "cookies";

interface PrivacyPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open on a specific tab (e.g. cookies for change-settings). */
  initialTab?: PrivacyPolicyTab;
}

const PrivacyPolicyDialog: React.FC<PrivacyPolicyDialogProps> = ({
  open,
  onOpenChange,
  initialTab = "privacy",
}) => {
  const { language } = useLanguage();
  const labels = footerLabels(language);
  const consentT = cookieConsentLabels(language);
  const [tab, setTab] = useState<PrivacyPolicyTab>(initialTab);
  const nativeSheetProps = useNativeExpandableSheetProps(open, {
    nativeClass: "native-legal-sheet native-sheet-expandable",
    className:
      "rounded-lg max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-0",
  });

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);
  type PrivacySectionBase = { title: string; [k: string]: unknown };
  type PrivacyContent = {
    lastUpdatedDate?: string;
    intro1?: string;
    intro2?: string;
    intro3?: string;
    sections?: {
      controller?: PrivacySectionBase & {
        p1?: string;
        p2?: string;
        contacts?: Array<{ label: string; value: string; href?: string }>;
      };
      data?: PrivacySectionBase & { p1?: string; list?: string[]; p2?: string };
      purpose?: PrivacySectionBase & {
        p1?: string;
        list?: string[];
        purposeCol?: string;
        legalCol?: string;
        rows?: string[][];
        p2?: string;
      };
      legalBasis?: PrivacySectionBase & { p1?: string; list?: string[] };
      recipients?: PrivacySectionBase & {
        p1?: string;
        purposeCol?: string;
        legalCol?: string;
        rows?: string[][];
        p2?: string;
        list?: string[];
      };
      personalNumber?: PrivacySectionBase & { p1?: string };
      storage?: PrivacySectionBase & { p1?: string; p2?: string };
      transfer?: PrivacySectionBase & { p1?: string; p2?: string };
      rights?: PrivacySectionBase & { p1?: string; p2?: string; p3?: string; list?: string[] };
      security?: PrivacySectionBase & { p1?: string };
      changes?: PrivacySectionBase & { p1?: string; p2?: string };
    };
    cookies?: {
      intro?: string;
      what?: PrivacySectionBase & { p1?: string };
      types?: PrivacySectionBase & {
        necessary?: string;
        necessaryDesc?: string;
        analytics?: string;
        analyticsDesc?: string;
        marketing?: string;
        marketingDesc?: string;
      };
      thirdParties?: PrivacySectionBase & { p1?: string };
      consent?: PrivacySectionBase & { p1?: string; list?: string[]; p2?: string };
      manage?: PrivacySectionBase & { p1?: string };
      contact?: PrivacySectionBase & { p1?: string; email?: string };
    };
  };

  const privacy = privacyLabels(language) as PrivacyContent;
  const lastUpdatedDate =
    (privacy?.lastUpdatedDate as string | undefined) ?? "29/03/2026";

  const SectionCard = ({
    title,
    children,
  }: {
    title?: string;
    children: React.ReactNode;
  }) => {
    if (!title) return null;
    return (
      <article className="rounded-xl border bg-muted/30 p-4 sm:p-5">
        <h2 className="text-base font-semibold tracking-tight mb-3">{title}</h2>
        {children}
      </article>
    );
  };

  const renderList = (list?: string[]) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return (
      <ul className="list-disc pl-6 space-y-1 mt-2 text-muted-foreground/90">
        {list.map((item, i) => (
          <li
            key={i}
            className="text-muted-foreground/90"
            dangerouslySetInnerHTML={{ __html: formatBold(item) }}
          />
        ))}
      </ul>
    );
  };

  const renderTable = (
    cols?: { purpose?: string; legal?: string },
    rows?: string[][]
  ) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-border text-xs">
          <thead>
            <tr className="bg-muted">
              <th className="border border-border p-2 text-left">
                {cols?.purpose}
              </th>
              <th className="border border-border p-2 text-left">{cols?.legal}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="border border-border p-2">{row?.[0]}</td>
                <td className="border border-border p-2">{row?.[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent {...nativeSheetProps} className={cn(nativeSheetProps.className)}>
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
          <DialogTitle>{labels.privacyDialog.title}</DialogTitle>
          <p className="text-sm text-muted-foreground font-normal">
            <strong>BILENIA Auktioner</strong>
            <br />
            {labels.privacyDialog?.lastUpdated || "Senast uppdaterad"}:{" "}
            {lastUpdatedDate}
          </p>
        </DialogHeader>
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as PrivacyPolicyTab)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-shrink-0 mb-4 px-4 sm:px-6 space-y-3">
            <TabsList className="grid w-full grid-cols-2 p-1 bg-muted">
              <TabsTrigger
                value="privacy"
                className="py-2.5 px-3 border-r border-border/40 data-[state=active]:border-r-0"
              >
                {labels.privacyDialog.privacyTab}
              </TabsTrigger>
              <TabsTrigger value="cookies" className="py-2.5 px-3">
                {labels.privacyDialog.cookiesTab}
              </TabsTrigger>
            </TabsList>
            {tab === "cookies" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto rounded-xl gap-2"
                onClick={() => {
                  onOpenChange(false);
                  // Let policy dialog close before consent UI opens
                  window.setTimeout(() => openCookieConsentSettings({ edit: true }), 180);
                }}
              >
                <Cookie className="h-3.5 w-3.5" />
                {consentT.changeSettings}
              </Button>
            ) : null}
          </div>

          <TabsContent
            value="privacy"
            className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-4 sm:pb-6 min-h-0"
          >
            <div className="space-y-5 text-sm pr-2">
              <header className="space-y-1">
                <p className="text-muted-foreground text-[11px]">
                  {labels.privacyDialog?.lastUpdated || "Senast uppdaterad"}:{" "}
                  {lastUpdatedDate}
                </p>
                {privacy?.intro1 ? (
                  <p
                    className="text-muted-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatBold(privacy.intro1) }}
                  />
                ) : null}
                {privacy?.intro2 ? (
                  <p className="text-muted-foreground/90 leading-relaxed">
                    {privacy.intro2}
                  </p>
                ) : null}
                {privacy?.intro3 ? (
                  <p className="text-muted-foreground/90 leading-relaxed">
                    {privacy.intro3}
                  </p>
                ) : null}
              </header>
              <div className="space-y-4">
                {privacy?.sections?.controller ? (
                  <SectionCard title={privacy.sections.controller.title}>
                    {privacy.sections.controller.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.controller.p1}
                      </p>
                    ) : null}
                    {privacy.sections.controller.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.controller.p2}
                      </p>
                    ) : null}
                    {Array.isArray(privacy.sections.controller.contacts) &&
                    privacy.sections.controller.contacts.length > 0 ? (
                      <ul className="list-disc pl-6 space-y-1 mt-2">
                        {privacy.sections.controller.contacts.map(
                          (c: { label: string; value: string; href?: string }, i: number) => (
                            <li key={i}>
                              {c.label}{" "}
                              {c.href ? (
                                <a
                                  href={c.href}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {c.value}
                                </a>
                              ) : (
                                <span className="font-medium">{c.value}</span>
                              )}
                            </li>
                          )
                        )}
                      </ul>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.data ? (
                  <SectionCard title={privacy.sections.data.title}>
                    {privacy.sections.data.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.data.p1}
                      </p>
                    ) : null}
                    {renderList(privacy.sections.data.list)}
                    {privacy.sections.data.p2 ? (
                      <p
                        className="mt-2 text-muted-foreground/90"
                        dangerouslySetInnerHTML={{
                          __html: formatBold(privacy.sections.data.p2 || ""),
                        }}
                      />
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.purpose ? (
                  <SectionCard title={privacy.sections.purpose.title}>
                    {privacy.sections.purpose.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.purpose.p1}
                      </p>
                    ) : null}
                    {renderList(privacy.sections.purpose.list)}
                    {privacy.sections.purpose.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.purpose.p2}
                      </p>
                    ) : null}
                    {privacy.sections.purpose.rows ? (
                      <div className="mt-3">
                        {renderTable(
                          {
                            purpose: privacy.sections.purpose.purposeCol,
                            legal: privacy.sections.purpose.legalCol,
                          },
                          privacy.sections.purpose.rows
                        )}
                      </div>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.legalBasis ? (
                  <SectionCard title={privacy.sections.legalBasis.title}>
                    {privacy.sections.legalBasis.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.legalBasis.p1}
                      </p>
                    ) : null}
                    {renderList(privacy.sections.legalBasis.list)}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.recipients ? (
                  <SectionCard title={privacy.sections.recipients.title}>
                    {privacy.sections.recipients.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.recipients.p1}
                      </p>
                    ) : null}
                    {privacy.sections.recipients.rows ? (
                      <div className="mt-3">
                        {renderTable(
                          {
                            purpose: privacy.sections.recipients.purposeCol,
                            legal: privacy.sections.recipients.legalCol,
                          },
                          privacy.sections.recipients.rows
                        )}
                      </div>
                    ) : null}
                    {privacy.sections.recipients.p2 ? (
                      <p className="mt-3 text-muted-foreground/90">
                        {privacy.sections.recipients.p2}
                      </p>
                    ) : null}
                    {renderList(privacy.sections.recipients.list)}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.personalNumber ? (
                  <SectionCard title={privacy.sections.personalNumber.title}>
                    {privacy.sections.personalNumber.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.personalNumber.p1}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.storage ? (
                  <SectionCard title={privacy.sections.storage.title}>
                    {privacy.sections.storage.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.storage.p1}
                      </p>
                    ) : null}
                    {privacy.sections.storage.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.storage.p2}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.transfer ? (
                  <SectionCard title={privacy.sections.transfer.title}>
                    {privacy.sections.transfer.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.transfer.p1}
                      </p>
                    ) : null}
                    {privacy.sections.transfer.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.transfer.p2}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.rights ? (
                  <SectionCard title={privacy.sections.rights.title}>
                    {privacy.sections.rights.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.rights.p1}
                      </p>
                    ) : null}
                    {privacy.sections.rights.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.rights.p2}
                      </p>
                    ) : null}
                    {privacy.sections.rights.p3 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.rights.p3}
                      </p>
                    ) : null}
                    {renderList(privacy.sections.rights.list)}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.security ? (
                  <SectionCard title={privacy.sections.security.title}>
                    {privacy.sections.security.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.security.p1}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}

                {privacy?.sections?.changes ? (
                  <SectionCard title={privacy.sections.changes.title}>
                    {privacy.sections.changes.p1 ? (
                      <p className="text-muted-foreground/90">
                        {privacy.sections.changes.p1}
                      </p>
                    ) : null}
                    {privacy.sections.changes.p2 ? (
                      <p className="mt-2 text-muted-foreground/90">
                        {privacy.sections.changes.p2}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}
              </div>
            </div>
          </TabsContent>

          <TabsContent
            value="cookies"
            className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-4 sm:pb-6 min-h-0"
          >
            <div className="space-y-5 text-sm pr-2">
              <header className="space-y-1">
                <p className="text-muted-foreground text-[11px]">
                  {labels.privacyDialog?.lastUpdated || "Senast uppdaterad"}:{" "}
                  {lastUpdatedDate}
                </p>
                {privacy?.cookies?.intro ? (
                  <p className="text-muted-foreground/90 leading-relaxed">
                    {privacy.cookies.intro}
                  </p>
                ) : null}
              </header>
              <div className="space-y-4">
                {privacy?.cookies?.what && (
                  <SectionCard title={privacy.cookies.what.title}>
                    <p className="text-muted-foreground/90">{privacy.cookies.what.p1}</p>
                  </SectionCard>
                )}

                {privacy?.cookies?.types && (
                  <SectionCard title={privacy.cookies.types.title}>
                    <div className="space-y-4">
                      {privacy.cookies.types.necessary ? (
                        <div>
                          <h3 className="text-sm font-semibold mb-2">
                            {privacy.cookies.types.necessary}
                          </h3>
                          <p className="text-muted-foreground/90">
                            {privacy.cookies.types.necessaryDesc}
                          </p>
                        </div>
                      ) : null}
                      {privacy.cookies.types.analytics ? (
                        <div>
                          <h3 className="text-sm font-semibold mb-2">
                            {privacy.cookies.types.analytics}
                          </h3>
                          <p className="text-muted-foreground/90">
                            {privacy.cookies.types.analyticsDesc}
                          </p>
                        </div>
                      ) : null}
                      {privacy.cookies.types.marketing ? (
                        <div>
                          <h3 className="text-sm font-semibold mb-2">
                            {privacy.cookies.types.marketing}
                          </h3>
                          <p className="text-muted-foreground/90">
                            {privacy.cookies.types.marketingDesc}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </SectionCard>
                )}

                {privacy?.cookies?.thirdParties ? (
                  <SectionCard title={privacy.cookies.thirdParties.title}>
                    <p className="text-muted-foreground/90">
                      {privacy.cookies.thirdParties.p1}
                    </p>
                  </SectionCard>
                ) : null}

                {privacy?.cookies?.consent && (
                  <SectionCard title={privacy.cookies.consent.title}>
                    <p
                      className="text-muted-foreground/90"
                      dangerouslySetInnerHTML={{
                        __html: formatBold(privacy.cookies.consent.p1 || ""),
                      }}
                    />
                    {renderList(privacy.cookies.consent.list)}
                    {privacy.cookies.consent.p2 ? (
                      <p
                        className="mt-2 text-muted-foreground/90"
                        dangerouslySetInnerHTML={{
                          __html: formatBold(privacy.cookies.consent.p2),
                        }}
                      />
                    ) : null}
                  </SectionCard>
                )}

                {privacy?.cookies?.manage && (
                  <SectionCard title={privacy.cookies.manage.title}>
                    <p className="text-muted-foreground/90">{privacy.cookies.manage.p1}</p>
                  </SectionCard>
                )}

                {privacy?.cookies?.contact && (
                  <SectionCard title={privacy.cookies.contact.title}>
                    <p className="text-muted-foreground/90">{privacy.cookies.contact.p1}</p>
                    {privacy.cookies.contact.email ? (
                      <ul className="list-disc pl-6 space-y-1 mt-2">
                        <li>
                          <a
                            href={`mailto:${privacy.cookies.contact.email}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {privacy.cookies.contact.email}
                          </a>
                        </li>
                      </ul>
                    ) : null}
                  </SectionCard>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;
